/**
 * One word for each membership state, everywhere under «Medlemmer» (2026-10-15).
 *
 * The register said «Aktivt», a person's period said «medlem», an application said «Medlem» and the import's
 * unreadable status said «Må rettes i Solidus» — four words, some for the same fact. The server's values are
 * unchanged (member-card `state`, membership_period, membership_request `state`); this file only maps them, so the
 * register list, the person page and «Til behandling» say the same thing about the same person.
 *
 *   Medlem       — an open membership (card state `active`, an open period)
 *   Utløpt       — the card's validity date has passed (card state `expired`)
 *   Utmeldt      — the person withdrew (a period closed by a withdrawal; an application `removed`)
 *   Avsluttet    — ended otherwise (missing from a complete import, ended by the mosque; card state `ended`)
 *   Ikke medlem  — in the register with no membership period
 *   Må avklares  — the import's status cannot be read, or says «utmeldt» while the membership is open
 *
 * An APPLICATION has its own words (it is not a membership yet): see REQUEST_NO.
 */
import type { TagTone } from '../components/ui';

export type Standing = 'member' | 'expired' | 'withdrawn' | 'ended' | 'not_member' | 'unclear';

export const STANDING: Record<Standing, [string, TagTone]> = {
  member: ['Medlem', 'green'],
  expired: ['Utløpt', 'gold'],
  withdrawn: ['Utmeldt', 'grey'],
  ended: ['Avsluttet', 'grey'],
  not_member: ['Ikke medlem', 'grey'],
  unclear: ['Må avklares', 'red'],
};

/** The register list and the person header without register access: core member-card `cardState().state`. */
export function fromCard(state?: string | null, status?: string | null): Standing {
  if (state === 'active') return 'member';
  if (state === 'expired') return 'expired';
  if (state === 'ended' || status === 'ended') return 'ended';
  return 'unclear';
}

/** How a closed period ended (membership_period.leave_method). */
export function fromLeave(leaveMethod?: string | null): Standing {
  return leaveMethod === 'withdrawal' ? 'withdrawn' : 'ended';
}

/** The person page with register access: the periods, newest last, and whether the import disagrees. */
export function fromPeriods(periods: { open?: boolean; leaveMethod?: string | null }[], needsReconciling: boolean): Standing {
  const open = periods.find((p) => p.open);
  if (open) return needsReconciling ? 'unclear' : 'member';
  const last = periods[periods.length - 1];
  return last ? fromLeave(last.leaveMethod) : 'not_member';
}

/** An application's state (membership_request.state) — about the application, so its own words. */
export const REQUEST_NO: Record<string, string> = {
  pending: 'Venter', removal_requested: 'Utmelding (eldre rad)', active: 'Medlem', rejected: 'Ikke godkjent', withdrawn: 'Søknaden trukket', removed: 'Utmeldt',
};
